import { getGroupingClassNames } from "@myaccount/_components/my-group-members/_client/get-grouping-class-names";
import { useEmailSelection } from "@myaccount/_components/my-group-members/_client/hooks/use-email-selection";
import { useI18n } from "@wojo/localization";
import { AlertBasic, Collapsible, Table, Text } from "@wojo/ui";
import {
    ConfirmedRequestedGroupMemberTripsFragment,
    GroupDepartureExtensionExcursionFragment,
    LineItemStatus,
} from "generated/graphql";
import React from "react";
import { EmailTravelersButton } from "../../email-travelers-button/EmailTravelersButton";
import { TravelerNameCheckbox } from "../../traveler-name-checkbox/TravelerNameCheckbox";
import styles from "./ExcursionsTable.module.scss";

export type ExcursionsProps = {
    excursions: GroupDepartureExtensionExcursionFragment[];
    bookedTravelers: ConfirmedRequestedGroupMemberTripsFragment[][];
    emailHelpTextId: string;
};

const getCollapsibleTitle = (
    excursion: GroupDepartureExtensionExcursionFragment,
    selectedCount: number,
    selectedTravelerText: string,
    bookedCount: number,
) => {
    return (
        <>
            <Text.Body emphasis={"2"}>
                {excursion.content?.name ?? ""}{" "}
                <span
                    style={{
                        color: "var(--s-color-success-standard)",
                        marginLeft: "var(--g-spacing-sm)",
                        fontWeight: "var(--g-font-weight-regular)",
                    }}
                >
                    {selectedCount > 0
                        ? ` (${selectedCount} ${selectedTravelerText})`
                        : ""}
                </span>
            </Text.Body>

            <Text.Body>{bookedCount} booked</Text.Body>
        </>
    );
};

export const ExcursionsTable: React.FC<ExcursionsProps> = (props) => {
    const { bookedTravelers, excursions, emailHelpTextId } = props;
    const i18n = useI18n("account").account.myGroupMembers.excursionsTable;
    const { selectedEmailsMap, allEmails, handleCheckboxChange } =
        useEmailSelection({
            confirmedTravelers: bookedTravelers,
        });
    const selectedEmails = Array.from(
        new Set(
            Array.from(selectedEmailsMap.values()).filter((email) => email),
        ),
    );

    const getExcursionStatusCell = (
        traveler: ConfirmedRequestedGroupMemberTripsFragment,
        excursion: GroupDepartureExtensionExcursionFragment,
    ) => {
        const lineItem = traveler.openExcursionLineItems.find(
            (x) => x.lineItemCode === excursion.lineItemCode,
        );
        if (lineItem?.status === LineItemStatus.Requested) {
            return (
                <AlertBasic
                    status={"info"}
                    title={i18n.requestedLabel.l()}
                    className={styles.alert}
                    iconAltText=""
                />
            );
        } else if (lineItem?.isPurchased) {
            return (
                <AlertBasic
                    status={"confirmed"}
                    title={i18n.bookedLabel.l()}
                    className={styles.alert}
                    iconAltText=""
                />
            );
        } else {
            return <span>{i18n.notBookedLabel.l()}</span>;
        }
    };

    const getBookedCount = (
        excursion: GroupDepartureExtensionExcursionFragment,
    ) => {
        return bookedTravelers
            .flat()
            .filter((traveler) =>
                traveler.openExcursionLineItems.some(
                    (lineItem) =>
                        lineItem.lineItemCode === excursion.lineItemCode &&
                        lineItem.isPurchased,
                ),
            ).length;
    };

    return (
        <>
            {excursions.map((excursion) => {
                const selectedCount = bookedTravelers
                    .flat()
                    .filter((traveler) =>
                        selectedEmailsMap.has(traveler._id + excursion._id),
                    ).length;
                const selectedTravelerText =
                    selectedCount === 1
                        ? i18n.oneTravelerSelected.l()
                        : i18n.multipleTravelersSelected.l();
                const bookedCount = getBookedCount(excursion);

                return (
                    <Collapsible
                        key={excursion._id}
                        title={getCollapsibleTitle(
                            excursion,
                            selectedCount,
                            selectedTravelerText,
                            bookedCount,
                        )}
                        className={styles.collapsible}
                    >
                        <div className={styles.table}>
                            <Table
                                aria-describedby={emailHelpTextId}
                                header={
                                    <Table.Header>
                                        <Table.Cell
                                            className={styles["header-cell"]}
                                        >
                                            {i18n.nameHeader.l()}
                                        </Table.Cell>
                                        <Table.Cell
                                            className={
                                                styles["offset-checkbox"]
                                            }
                                        >
                                            {i18n.excursionsHeader.l()}
                                        </Table.Cell>
                                    </Table.Header>
                                }
                                showTitle={false}
                                tableTitle={
                                    <Text.Heading
                                        order="3"
                                        style={{
                                            paddingBottom:
                                                "var(--g-spacing-md)",
                                        }}
                                    >
                                        {i18n.tableTitle.l()}
                                    </Text.Heading>
                                }
                            >
                                {bookedTravelers.map((group) =>
                                    group.map((traveler, travelerIndex) => {
                                        const email =
                                            traveler.customer?.email ?? "";
                                        const classNames =
                                            getGroupingClassNames(
                                                travelerIndex,
                                                group.length,
                                            );
                                        return (
                                            <Table.Row
                                                key={traveler._id}
                                                className={classNames}
                                                header={
                                                    <Table.Cell
                                                        className={classNames}
                                                    >
                                                        <TravelerNameCheckbox
                                                            id={
                                                                traveler._id +
                                                                excursion._id
                                                            }
                                                            email={email}
                                                            selectedEmailsMap={
                                                                selectedEmailsMap
                                                            }
                                                            handleEmailCheckboxChange={
                                                                handleCheckboxChange
                                                            }
                                                            firstName={
                                                                traveler
                                                                    .customer
                                                                    ?.firstName ??
                                                                ""
                                                            }
                                                            lastName={
                                                                traveler
                                                                    .customer
                                                                    ?.lastName ??
                                                                ""
                                                            }
                                                        />
                                                    </Table.Cell>
                                                }
                                            >
                                                <Table.Cell>
                                                    {getExcursionStatusCell(
                                                        traveler,
                                                        excursion,
                                                    )}
                                                </Table.Cell>
                                            </Table.Row>
                                        );
                                    }),
                                )}
                            </Table>
                        </div>
                    </Collapsible>
                );
            })}
            <EmailTravelersButton
                emails={selectedEmails}
                allEmails={allEmails}
            />
        </>
    );
};
